import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

const INV_SELECT = `
  inventory_id, variant_id, quantity, condition,
  status_for_trade, status_own,
  status_want_in_trade, status_want_to_buy,
  lure_variants (
    variant_id, color_name, variant_name, image_url,
    base_lures (
      base_lure_id, name,
      brands (brand_name)
    )
  )
`

// wantedByOtherIds = Set of variant_ids the OTHER user wants (want_in_trade or want_to_buy)
function sortInventory(items, wantedByOtherIds) {
  const getPriority = (item) => {
    if (item.status_for_trade && wantedByOtherIds.has(item.variant_id)) return 0
    if (item.status_for_trade) return 1
    if (item.status_own) return 2
    return 3
  }

  return [...items].sort((a, b) => {
    const diff = getPriority(a) - getPriority(b)
    if (diff !== 0) return diff
    const nameA = a.lure_variants?.base_lures?.name || ''
    const nameB = b.lure_variants?.base_lures?.name || ''
    return nameA.localeCompare(nameB)
  })
}

function getItemPriority(item, wantedByOtherIds) {
  if (item.status_for_trade && wantedByOtherIds.has(item.variant_id)) return 0
  if (item.status_for_trade) return 1
  if (item.status_own) return 2
  return 3
}

const PRIORITY_LABELS = [
  'For Trade — Matches Their Wants',
  'For Trade',
  'Owned',
  'Other',
]

const OTHER_PRIORITY_LABELS = [
  'For Trade — Matches Your Wants',
  'For Trade',
  'Owned',
  'Other',
]

function InventoryColumn({ title, items, wantedByOtherIds, selected, onToggle, priorityLabels }) {
  const sorted = useMemo(() => sortInventory(items, wantedByOtherIds), [items, wantedByOtherIds])

  // Group by priority
  const groups = [[], [], [], []]
  sorted.forEach(item => {
    const p = getItemPriority(item, wantedByOtherIds)
    groups[p].push(item)
  })

  return (
    <div className="propose-column">
      <h3 className="propose-column-title">{title}</h3>
      {items.length === 0 ? (
        <p className="propose-empty">No inventory items.</p>
      ) : (
        groups.map((group, pi) => {
          if (group.length === 0) return null
          return (
            <div key={pi} className="propose-group">
              <div className={`propose-group-label priority-${pi}`}>
                {priorityLabels[pi]}
              </div>
              {group.map(item => {
                const v = item.lure_variants
                const lure = v?.base_lures
                const isChecked = selected.has(item.inventory_id)
                return (
                  <label key={item.inventory_id} className={`propose-item${isChecked ? ' propose-item-selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onToggle(item.inventory_id)}
                      className="propose-checkbox"
                    />
                    <div className="propose-item-info">
                      {v?.image_url && (
                        <img src={v.image_url} alt={v.color_name} className="propose-thumb" />
                      )}
                      <div className="propose-item-text">
                        <span className="propose-lure-name">{lure?.name || '—'}</span>
                        <span className="propose-color-name">{v?.variant_name || v?.color_name || '—'}</span>
                        <span className="propose-brand">{lure?.brands?.brand_name || ''}</span>
                      </div>
                      {item.status_for_trade && (
                        <span className="propose-status-for-trade">For Trade</span>
                      )}
                    </div>
                  </label>
                )
              })}
            </div>
          )
        })
      )}
    </div>
  )
}

function ProposeTrade() {
  const { userId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [otherUser, setOtherUser] = useState(null)
  const [myInventory, setMyInventory] = useState([])
  const [theirInventory, setTheirInventory] = useState([])
  const [myWantedVariantIds, setMyWantedVariantIds] = useState(new Set())
  const [theirWantedVariantIds, setTheirWantedVariantIds] = useState(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  const [mySelected, setMySelected] = useState(new Set())
  const [theirSelected, setTheirSelected] = useState(new Set())

  useEffect(() => {
    if (!user) return
    if (user.id === userId) {
      setError("You can't propose a trade with yourself.")
      setLoading(false)
      return
    }
    fetchData()
  }, [user, userId])

  const fetchData = async () => {
    try {
      const WANTS_FILTER = 'status_want_in_trade.eq.true,status_want_to_buy.eq.true'

      const [profileRes, myInvRes, theirInvRes, myWantsRes, theirWantsRes] = await Promise.all([
        supabase.from('public_user_profiles').select('id, username').eq('id', userId).single(),
        supabase.from('user_lure_inventory').select(INV_SELECT).eq('user_id', user.id).or('status_own.eq.true,status_for_trade.eq.true'),
        supabase.from('user_lure_inventory').select(INV_SELECT).eq('user_id', userId).or('status_own.eq.true,status_for_trade.eq.true'),
        supabase.from('user_lure_inventory').select('variant_id').eq('user_id', user.id).or(WANTS_FILTER),
        supabase.from('user_lure_inventory').select('variant_id').eq('user_id', userId).or(WANTS_FILTER),
      ])

      if (profileRes.error) throw new Error('User not found')

      setOtherUser(profileRes.data)
      setMyInventory(myInvRes.data || [])
      setTheirInventory(theirInvRes.data || [])
      setMyWantedVariantIds(new Set((myWantsRes.data || []).map(w => w.variant_id)))
      setTheirWantedVariantIds(new Set((theirWantsRes.data || []).map(w => w.variant_id)))
    } catch (err) {
      console.error('Error loading trade data:', err)
      setError(err.message || 'Failed to load trade data')
    } finally {
      setLoading(false)
    }
  }

  const toggleMy = (id) => {
    setMySelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleTheir = (id) => {
    setTheirSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const getItemLabel = (invId, inventory) => {
    const item = inventory.find(i => i.inventory_id === invId)
    if (!item) return '—'
    const v = item.lure_variants
    const lure = v?.base_lures
    return `${lure?.name || '?'} — ${v?.variant_name || v?.color_name || '?'}`
  }

  const buildMessageBody = () => {
    const offering = [...mySelected].map(id => `• ${getItemLabel(id, myInventory)}`).join('\n')
    const requesting = [...theirSelected].map(id => `• ${getItemLabel(id, theirInventory)}`).join('\n')
    return (
      `TRADE OFFER 📦\n\n` +
      `I'm offering:\n${offering || '(nothing selected)'}\n\n` +
      `In exchange for:\n${requesting || '(nothing selected)'}\n\n` +
      `Please use the Accept or Decline buttons to respond to this offer.`
    )
  }

  const handleSubmitOffer = async () => {
    if (mySelected.size === 0 && theirSelected.size === 0) {
      setError('Please select at least one item on each side of the trade.')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      // 1. Create trade offer record
      const { data: offer, error: offerErr } = await supabase
        .from('trade_offers')
        .insert({ proposer_id: user.id, recipient_id: userId })
        .select('trade_offer_id')
        .single()
      if (offerErr) throw offerErr

      // 2. Insert trade offer items
      const items = [
        ...[...mySelected].map(inventory_id => ({
          trade_offer_id: offer.trade_offer_id,
          inventory_id,
          side: 'offering',
        })),
        ...[...theirSelected].map(inventory_id => ({
          trade_offer_id: offer.trade_offer_id,
          inventory_id,
          side: 'requesting',
        })),
      ]
      const { error: itemsErr } = await supabase.from('trade_offer_items').insert(items)
      if (itemsErr) throw itemsErr

      // 3. Create a new conversation linked to this trade offer
      const [p1, p2] = [user.id, userId].sort()
      const { data: conv, error: convErr } = await supabase
        .from('conversations')
        .insert({ participant_1: p1, participant_2: p2, trade_id: offer.trade_offer_id })
        .select('conversation_id')
        .single()
      if (convErr) throw convErr

      // 4. Insert the trade offer message
      const body = buildMessageBody()
      const { error: msgErr } = await supabase
        .from('messages')
        .insert({
          conversation_id: conv.conversation_id,
          sender_id: user.id,
          body,
          is_trade_offer: true,
          trade_offer_id: offer.trade_offer_id,
        })
      if (msgErr) throw msgErr

      navigate(`/messages/${conv.conversation_id}`)
    } catch (err) {
      console.error('Error submitting trade offer:', err)
      setError(err.message || 'Failed to send trade offer')
      setSubmitting(false)
    }
  }

  if (!user) {
    return (
      <div className="error-page">
        <h2>Login Required</h2>
        <Link to="/login" className="btn-primary">Log In</Link>
      </div>
    )
  }

  if (loading) return <div className="loading">Loading trade builder...</div>

  if (error && !otherUser) {
    return (
      <div className="page-container">
        <div className="error-message">{error}</div>
        <Link to="/trade/browse" className="btn-secondary" style={{ marginTop: '1rem', display: 'inline-block' }}>
          ← Back to Browse Trade
        </Link>
      </div>
    )
  }

  const canPreview = mySelected.size > 0 || theirSelected.size > 0

  return (
    <div className="page-container">
      <div className="propose-header">
        <Link to="/trade/browse" className="back-link">← Browse Trade</Link>
        <h1>Propose Trade with <span className="propose-other-name">{otherUser?.username}</span></h1>
        <p className="propose-instructions">
          Select items you want to offer (left) and items you'd like to receive (right), then preview your offer.
        </p>
        {error && <div className="alert-error">{error}</div>}
      </div>

      <div className="propose-columns">
        <InventoryColumn
          title="Your Items"
          items={myInventory}
          wantedByOtherIds={theirWantedVariantIds}
          selected={mySelected}
          onToggle={toggleMy}
          priorityLabels={PRIORITY_LABELS}
        />

        <div className="propose-divider">⇄</div>

        <InventoryColumn
          title={`${otherUser?.username}'s Items`}
          items={theirInventory}
          wantedByOtherIds={myWantedVariantIds}
          selected={theirSelected}
          onToggle={toggleTheir}
          priorityLabels={OTHER_PRIORITY_LABELS}
        />
      </div>

      <div className="propose-footer">
        <span className="propose-selected-count">
          {mySelected.size} offering · {theirSelected.size} requesting
        </span>
        <button
          className="btn-primary"
          onClick={() => setShowPreview(true)}
          disabled={!canPreview}
        >
          Preview Trade Offer →
        </button>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content propose-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Review Trade Offer</h2>
              <button onClick={() => setShowPreview(false)} className="modal-close">✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#475569', marginBottom: '1rem' }}>
                Proposing a trade with <strong>{otherUser?.username}</strong>. Review the details below, then send.
              </p>
              <div className="preview-columns">
                <div className="preview-side">
                  <h4 className="preview-side-label">You Offer</h4>
                  {mySelected.size === 0
                    ? <p className="preview-none">Nothing selected</p>
                    : [...mySelected].map(id => (
                      <div key={id} className="preview-item">{getItemLabel(id, myInventory)}</div>
                    ))
                  }
                </div>
                <div className="preview-arrow">⇄</div>
                <div className="preview-side">
                  <h4 className="preview-side-label">You Receive</h4>
                  {theirSelected.size === 0
                    ? <p className="preview-none">Nothing selected</p>
                    : [...theirSelected].map(id => (
                      <div key={id} className="preview-item">{getItemLabel(id, theirInventory)}</div>
                    ))
                  }
                </div>
              </div>
              {error && <div className="alert-error" style={{ marginTop: '1rem' }}>{error}</div>}
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowPreview(false)} className="btn-secondary" disabled={submitting}>
                Back to Edit
              </button>
              <button onClick={handleSubmitOffer} className="btn-primary" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Trade Offer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProposeTrade
